import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { createRegistration, getEvents, getProfile, getRegistrations } from "../api/api";
import AdherentShowcase from "../components/AdherentShowcase";
import { useNotifications } from "../components/NotificationsContext";

function imageUrl(ev) {
  if (!ev?.image) return null;
  if (ev.image.startsWith("http://") || ev.image.startsWith("https://")) return ev.image;
  return `http://localhost:8000${ev.image}`;
}

function prixNet(ev) {
  const p = Number(ev?.prix || 0);
  const s = Number(ev?.subvention_pourcent || 0);
  return (p - (p * s) / 100).toFixed(2);
}

function eventDate(ev) {
  const value = ev?.date_evenement || ev?.date_debut;
  return value ? new Date(value).toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }) : "-";
}

function isTodayOrFuture(value) {
  if (!value) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(value) >= today;
}

function eventStatus(ev, registrations) {
  const hasRegistration = registrations.some((r) => r.event === ev.id);
  if (hasRegistration) return "registered";
  const capacity = Number(ev?.capacite_max || 0);
  const registered = Number(ev?.nb_inscrits || 0);
  if (capacity > 0 && registered >= capacity) return "full";
  if ((ev?.date_evenement || ev?.date_fin) && new Date(ev.date_evenement || ev.date_fin) < new Date()) return "past";
  return "open";
}

function statusBadge(status) {
  if (status === "registered") return <Badge bg="success">Deja inscrit</Badge>;
  if (status === "full") return <Badge bg="danger">Complet</Badge>;
  if (status === "past") return <Badge bg="secondary">Archive</Badge>;
  return <Badge bg="warning" text="dark">Ouvert</Badge>;
}

export default function EventsAdherent() {
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectSelf, setSelectSelf] = useState(false);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { addNotification } = useNotifications();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, regsRes, profileRes] = await Promise.all([
        getEvents(),
        getRegistrations(),
        getProfile(),
      ]);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data.filter((event) => isTodayOrFuture(event.date_evenement || event.date_debut)) : []);
      setRegistrations(Array.isArray(regsRes.data) ? regsRes.data : []);
      setProfile(profileRes.data);
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de charger les evenements, inscriptions ou profil.",
        delay: 7000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const placesRestantesForEvent = (ev) => {
    const capacity = Number(ev?.capacite_max || 0);
    if (!capacity) return null;
    return capacity - Number(ev?.nb_inscrits || 0);
  };

  const isSelfRegistered = (eventId) =>
    registrations.some((r) => r.event === eventId && r.participant_adherent);

  const isChildRegistered = (eventId, childId) =>
    registrations.some((r) => r.event === eventId && r.participant_enfant === childId);

  const isParentRegistered = (eventId, parentId) =>
    registrations.some((r) => r.event === eventId && r.participant_parent === parentId);

  const eligibleChildren = useMemo(() => {
    if (!selectedEvent?.autorise_enfant) return [];
    const min = selectedEvent.age_min_enfant ?? null;
    const max = selectedEvent.age_max_enfant ?? null;

    return (profile?.enfants || [])
      .filter((child) => {
        const age = child.age ?? null;
        if (age === null) return true;
        if (min !== null && age < min) return false;
        if (max !== null && age > max) return false;
        return true;
      })
      .filter((child) => !isChildRegistered(selectedEvent.id, child.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, selectedEvent, registrations]);

  const eligibleParents = useMemo(() => {
    if (!selectedEvent?.autorise_parent) return [];
    return (profile?.parents || [])
      .filter((parent) => parent.situation === "vivant")
      .filter((parent) => !isParentRegistered(selectedEvent.id, parent.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, selectedEvent, registrations]);

  const selectionCount = () => (selectSelf ? 1 : 0) + selectedChildIds.length + selectedParentIds.length;

  const openModal = (ev) => {
    const status = eventStatus(ev, registrations);
    const places = placesRestantesForEvent(ev);

    if (status === "registered") {
      addNotification({ type: "info", title: "Deja inscrit", message: "Vous avez deja une inscription pour cet evenement.", delay: 4000 });
      return;
    }
    if (places !== null && places <= 0) {
      addNotification({ type: "info", title: "Complet", message: "Cet evenement est deja complet.", delay: 4000 });
      return;
    }

    setSelectedEvent(ev);
    setSelectSelf(false);
    setSelectedChildIds([]);
    setSelectedParentIds([]);
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (!profile?.id || !selectedEvent) return;

    const total = selectionCount();
    if (total <= 0) {
      addNotification({ type: "error", title: "Erreur", message: "Selectionnez au moins une personne.", delay: 6000 });
      return;
    }

    const places = placesRestantesForEvent(selectedEvent);
    if (places !== null && places < total) {
      addNotification({
        type: "error",
        title: "Places insuffisantes",
        message: `Il reste ${places} place(s), vous avez selectionne ${total}.`,
        delay: 7000,
      });
      return;
    }

    const payloads = [];
    if (selectSelf && !isSelfRegistered(selectedEvent.id)) {
      payloads.push({ event: selectedEvent.id, participant_adherent: profile.id });
    }
    selectedChildIds.forEach((childId) => {
      if (!isChildRegistered(selectedEvent.id, childId)) {
        payloads.push({ event: selectedEvent.id, participant_enfant: childId });
      }
    });
    selectedParentIds.forEach((parentId) => {
      if (!isParentRegistered(selectedEvent.id, parentId)) {
        payloads.push({ event: selectedEvent.id, participant_parent: parentId });
      }
    });

    if (payloads.length === 0) {
      addNotification({ type: "info", title: "Aucune inscription", message: "Les personnes selectionnees sont deja inscrites.", delay: 6000 });
      return;
    }

    setSubmitting(true);
    try {
      const results = await Promise.all(payloads.map((payload) => createRegistration(payload)));
      const created = results.map((res) => res.data);
      setRegistrations((prev) => [...prev, ...created]);
      setEvents((prev) =>
        prev.map((event) =>
          event.id === selectedEvent.id
            ? { ...event, nb_inscrits: Number(event.nb_inscrits || 0) + created.length }
            : event
        )
      );
      addNotification({ type: "success", title: "Inscription", message: `${created.length} inscription(s) enregistree(s).`, delay: 5000 });
      setShowModal(false);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        "Impossible de creer l'inscription.";
      addNotification({ type: "error", title: "Erreur", message: msg, delay: 7000 });
    } finally {
      setSubmitting(false);
    }
  };

  const renderHero = (ev) => {
    const status = eventStatus(ev, registrations);
    const places = placesRestantesForEvent(ev);

    return (
      <article className="showcase-hero">
        <div className="showcase-hero-media">
          {imageUrl(ev) ? (
            <img src={imageUrl(ev)} alt={ev.titre} />
          ) : (
            <div className="showcase-hero-media-placeholder">EV</div>
          )}
        </div>
        <div className="showcase-hero-content">
          <div>{statusBadge(status)}</div>
          <h3>{ev.titre}</h3>
          <p>{ev.description || ev.lieu || "Nouvel evenement disponible pour les adherents."}</p>
          <div className="showcase-meta">
            <span className="showcase-pill">{ev.lieu || "Lieu non defini"}</span>
            <span className="showcase-pill">{eventDate(ev)}</span>
            <span className="showcase-pill">{prixNet(ev)} DT</span>
            <span className="showcase-pill">
              {places === null ? "Capacite libre" : `${Math.max(places, 0)} places restantes`}
            </span>
          </div>
          <div className="showcase-actions">
            <Button variant="primary" onClick={() => openModal(ev)} disabled={status !== "open"}>
              {status === "registered" ? "Deja inscrit" : status === "full" ? "Complet" : "S'inscrire"}
            </Button>
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <AdherentShowcase
        title="Evenements"
        subtitle="Les evenements les plus recents sont mis en avant. Le tableau dessous affiche tous les evenements."
        items={events}
        searchPlaceholder="Rechercher par titre ou lieu..."
        emptyText="Aucun evenement disponible pour le moment."
        showArchive={false}
        getDate={(ev) => ev.date_evenement || ev.date_debut}
        getStatus={(ev) => eventStatus(ev, registrations)}
        getSearchText={(ev) => `${ev.titre || ""} ${ev.lieu || ""} ${ev.description || ""}`}
        statusOptions={[
          { value: "open", label: "Ouvert" },
          { value: "registered", label: "Deja inscrit" },
          { value: "full", label: "Complet" },
          { value: "past", label: "Archive" },
        ]}
        renderHero={renderHero}
      />

      <Card className="mt-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div>
              <span className="showcase-eyebrow">Tableau</span>
              <h4 className="mb-0" style={{ fontWeight: 950 }}>Tous les evenements</h4>
            </div>
          </div>

          {events.length === 0 ? (
            <Alert variant="warning" className="mb-0">Aucun evenement disponible.</Alert>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Evenement</th>
                  <th>Date</th>
                  <th>Prix</th>
                  <th>Places</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {[...events]
                  .sort((a, b) => new Date((a.date_evenement || a.date_debut) || 0) - new Date((b.date_evenement || b.date_debut) || 0))
                  .map((ev) => {
                    const status = eventStatus(ev, registrations);
                    const places = placesRestantesForEvent(ev);

                    return (
                      <tr key={ev.id}>
                        <td style={{ minWidth: 260 }}>
                          <div className="d-flex align-items-center gap-3">
                            {imageUrl(ev) ? (
                              <img
                                src={imageUrl(ev)}
                                alt={ev.titre}
                                style={{ width: 76, height: 52, objectFit: "cover", borderRadius: 14 }}
                              />
                            ) : (
                              <div className="showcase-row-placeholder" style={{ width: 76, height: 52 }}>EV</div>
                            )}
                            <div>
                              <strong>{ev.titre}</strong>
                              <div className="text-muted" style={{ fontSize: 13 }}>{ev.lieu || "-"}</div>
                            </div>
                          </div>
                        </td>
                        <td>{eventDate(ev)}</td>
                        <td>{prixNet(ev)} DT</td>
                        <td>{places === null ? "Libre" : Math.max(places, 0)}</td>
                        <td>{statusBadge(status)}</td>
                        <td>
                          <Button size="sm" variant="outline-primary" onClick={() => openModal(ev)} disabled={status !== "open"}>
                            Ouvrir
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Inscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Evenement : <strong>{selectedEvent?.titre}</strong></p>

          <h6>Qui inscrire ?</h6>

          {selectedEvent?.autorise_adherent && (
            <Form.Check
              type="checkbox"
              className="mb-2"
              label={isSelfRegistered(selectedEvent.id) ? "Moi (deja inscrit)" : "Moi"}
              checked={selectSelf}
              disabled={isSelfRegistered(selectedEvent.id)}
              onChange={(e) => setSelectSelf(e.target.checked)}
            />
          )}

          {selectedEvent?.autorise_enfant && (
            <div className="mb-3">
              <strong>Enfants</strong>
              {eligibleChildren.length === 0 ? (
                <Alert variant="light" className="py-2 mt-2">Aucun enfant eligible ou deja inscrit.</Alert>
              ) : (
                eligibleChildren.map((child) => (
                  <Form.Check
                    key={child.id}
                    type="checkbox"
                    className="mb-1"
                    label={`${child.prenom} ${child.nom} (age: ${child.age ?? "?"})`}
                    checked={selectedChildIds.includes(child.id)}
                    onChange={() =>
                      setSelectedChildIds((prev) =>
                        prev.includes(child.id) ? prev.filter((x) => x !== child.id) : [...prev, child.id]
                      )
                    }
                  />
                ))
              )}
            </div>
          )}

          {selectedEvent?.autorise_parent && (
            <div className="mb-2">
              <strong>Parents</strong>
              {eligibleParents.length === 0 ? (
                <Alert variant="light" className="py-2 mt-2">Aucun parent vivant disponible ou deja inscrit.</Alert>
              ) : (
                eligibleParents.map((parent) => (
                  <Form.Check
                    key={parent.id}
                    type="checkbox"
                    className="mb-1"
                    label={`${parent.type_parent === "pere" ? "Pere" : "Mere"}: ${parent.prenom} ${parent.nom}`}
                    checked={selectedParentIds.includes(parent.id)}
                    onChange={() =>
                      setSelectedParentIds((prev) =>
                        prev.includes(parent.id) ? prev.filter((x) => x !== parent.id) : [...prev, parent.id]
                      )
                    }
                  />
                ))
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div className="text-muted">Selection: <strong>{selectionCount()}</strong></div>
          <div>
            <Button variant="secondary" onClick={() => setShowModal(false)} className="me-2">Annuler</Button>
            <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
              {submitting ? "Enregistrement..." : "Confirmer"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
